// THE TEMPLATE, DRAWN AS ONE PROPERTY RUNS IT.
//
// The designer board takes nodes and routes that carry their own credentials. That is what a saved
// integration used to be, and it is still exactly what an IMPLEMENTATION is — the same shape, with one
// property's credentials in the slots. Since V508 the two were pulled apart: the template keeps the
// shape, an implementation keeps the credentials, and nothing put them back together, so the only view
// of a running pipeline was a row saying "3 of 4".
//
// This puts them back together for DISPLAY. It returns a new graph and never touches the template's
// own: what is drawn here is the template as this property runs it, and a mutation would make the
// Design tab show one property's credentials as though they belonged to the design.
//
// MATCHED BY ID, in both directions — `node_id` to a node, `route_id` to a route. A binding names
// exactly one of them (the table's own rule), so a row that names neither, or one whose slot has since
// been removed from the canvas, is simply not merged rather than being guessed at.

const indexBindings = (bindings) => {
    const byNode = new Map();
    const byRoute = new Map();
    for (const b of bindings || []) {
        if (b.node_id) byNode.set(String(b.node_id), b);
        else if (b.route_id) byRoute.set(String(b.route_id), b);
    }
    return { byNode, byRoute };
};

/**
 * @param {object} graph            the template's graph: { nodes, routes, ... }
 * @param {object} implementation   { bindings: [...] } as the server returns it
 * @returns {object} the same graph with each slot filled from its binding
 */
export const graphWithBindings = (graph, implementation) => {
    if (!graph) return graph;
    const { byNode, byRoute } = indexBindings(implementation && implementation.bindings);
    // WHICH CALLER EACH ROUTE IS MADE AS (2026-11-04). The default is the implementation's pipeline credential and
    // a route is absent from this map; a direct-UAPI vendor source's route (DailyPoint) carries the vendor's own
    // credential here, resolved for the hotel by the server, or null when the vendor is not yet granted. The board
    // binds against and warns against this, so it must be merged even when nothing is bound yet.
    const rsc = (implementation && implementation.routeSourceCredentials) || null;
    if (!byNode.size && !byRoute.size && !rsc) return graph;

    return {
        ...graph,
        nodes: (graph.nodes || []).map(n => {
            const b = byNode.get(String(n.id));
            if (!b) return n;
            return {
                ...n,
                credential_id: b.credential_id,
                credential_name: b.credential_name,
                // The property code at the source, where this property needs one of its own.
                hotelid_override: b.hotelid_override || n.hotelid_override,
                // THE REST OF THE PER-PROPERTY SETTINGS (V511). The node's are the template's
                // defaults; this property's answer wins where it has one.
                credential_overrides: b.credential_overrides || n.credential_overrides,
            };
        }),
        routes: (graph.routes || []).map(r => {
            // The caller this route is made as, when it is not the pipeline credential (a vendor source). `rsc` holds
            // a vendor-source route's { credentialId, granted }; absent (undefined) means the ordinary pipeline
            // credential applies. Present on every route so an unbound vendor-source route is still shown and bound
            // as the vendor, and `granted` tells the board whether binding it must grant the property first.
            const rscEntry = rsc && (String(r.id) in rsc) ? rsc[String(r.id)] : undefined;
            const source_credential_id = rscEntry ? rscEntry.credentialId : undefined;
            const source_credential_granted = rscEntry ? rscEntry.granted : undefined;
            const vendorFields = rscEntry ? { source_credential_id, source_credential_granted } : null;
            const b = byRoute.get(String(r.id));
            if (!b) return vendorFields ? { ...r, ...vendorFields } : r;
            return {
                ...r,
                ...(vendorFields || {}),
                credential_id: b.credential_id,
                credential_name: b.credential_name,
                // WHERE IT LANDS, for this property. The board resolves the backend's name and logo
                // from `access` when it has the binding's id, so handing over the id is enough — and
                // x_hotelid comes straight off the row so the address is right even before that
                // resolves.
                uapi_access_id: b.uapi_access_id,
                x_hotelid: b.x_hotelid || r.x_hotelid,
                // The binding's system, not the template route's — what the board names and checks against
                // the route's designed target (IntegrationDesigner misbound()).
                access_marketplace_id: b.uapi_access_id ? b.access_marketplace_id : r.access_marketplace_id,
                backend_systems: b.uapi_access_id ? b.backend_systems : r.backend_systems,
                // How this property's target result is viewed (V583) — the board's viewer button.
                backend_result_viewer: b.uapi_access_id ? b.backend_result_viewer : r.backend_result_viewer,
                // The custom code on that binding's backend (V528) — what the Integration column shows
                // runs around the integration for this property.
                uapi_custom_code: b.uapi_custom_code || [],
            };
        }),
    };
};

/** How many of the template's slots this implementation has a credential in. */
export const filledCount = (implementation) =>
    (implementation && implementation.bindings || []).filter(b => b.credential_id).length;
